import { createApi } from '@reduxjs/toolkit/query/react';
import { createAppBaseQuery } from './baseQuery';

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: createAppBaseQuery('/api/v1/admin'),
  tagTypes: [
    'Cases', 'Merchants', 'Users', 'FeatureFlags', 'Programs', 'Deals',
    'Analytics', 'AuditLogs', 'Categories', 'Appeals',
  ],
  endpoints: (builder) => ({
    listCases: builder.query({
      query: () => '/moderation/cases',
      providesTags: ['Cases'],
    }),
    takeAction: builder.mutation({
      query: ({ caseId, ...body }) => ({ url: `/moderation/cases/${caseId}/actions`, method: 'POST', body }),
      invalidatesTags: ['Cases'],
    }),
    listAppeals: builder.query({
      query: (params) => ({ url: '/appeals', params }),
      providesTags: ['Appeals'],
    }),
    reviewAppeal: builder.mutation({
      query: ({ appealId, ...body }) => ({
        url: `/appeals/${appealId}/review`,
        method: 'POST',
        body: {
          status: body.status,
          reviewNotes: body.reviewNotes ?? body.review_notes,
        },
      }),
      invalidatesTags: ['Appeals'],
    }),
    listMerchants: builder.query({
      query: () => '/merchants',
      providesTags: ['Merchants'],
    }),
    createMerchant: builder.mutation({
      query: (body) => ({
        url: '/merchants',
        method: 'POST',
        body: {
          name: body.name,
          slug: body.slug,
          domain: body.domain,
          description: body.description,
          logoUrl: body.logo_url || body.logoUrl,
          isActive: body.is_active ?? body.isActive ?? true,
        },
      }),
      invalidatesTags: ['Merchants'],
    }),
    updateMerchant: builder.mutation({
      query: ({ merchantId, ...body }) => ({
        url: `/merchants/${merchantId}`,
        method: 'PATCH',
        body: {
          name: body.name,
          slug: body.slug,
          domain: body.domain,
          description: body.description,
          logoUrl: body.logo_url || body.logoUrl,
          isActive: body.is_active ?? body.isActive,
        },
      }),
      invalidatesTags: ['Merchants'],
    }),
    listUsers: builder.query({
      query: (params) => ({ url: '/users', params }),
      providesTags: ['Users'],
    }),
    updateUser: builder.mutation({
      query: ({ userId, ...body }) => ({
        url: `/users/${userId}`,
        method: 'PATCH',
        body: {
          role: body.role,
          displayName: body.display_name || body.displayName,
          reputationScore: body.reputation_score ?? body.reputationScore,
        },
      }),
      invalidatesTags: ['Users'],
    }),
    listFeatureFlags: builder.query({
      query: () => '/feature-flags',
      providesTags: ['FeatureFlags'],
    }),
    toggleFeatureFlag: builder.mutation({
      query: ({ key, enabled }) => ({ url: `/feature-flags/${key}`, method: 'PATCH', body: { enabled } }),
      invalidatesTags: ['FeatureFlags'],
    }),
    listAffiliatePrograms: builder.query({
      query: () => '/affiliate-programs',
      providesTags: ['Programs'],
    }),
    updateAffiliateProgram: builder.mutation({
      query: ({ programId, ...body }) => ({
        url: `/affiliate-programs/${programId}`,
        method: 'PATCH',
        body: {
          isActive: body.is_active ?? body.isActive ?? (body.status === 'active'),
          commissionRate: body.commission_rate ?? body.commissionRate,
          commissionType: body.commission_type ?? body.commissionType,
          cookieDays: body.cookie_days ?? body.cookieDays,
          terms: body.notes ?? body.terms,
        },
      }),
      invalidatesTags: ['Programs'],
    }),
    listAllDeals: builder.query({
      query: (params) => ({ url: '/deals', params }),
      providesTags: ['Deals'],
    }),
    expireDeal: builder.mutation({
      query: (publicId) => ({ url: `/deals/${publicId}/expire`, method: 'POST' }),
      invalidatesTags: ['Deals', 'Analytics'],
    }),
    publishDeal: builder.mutation({
      query: ({ publicId, ...body }) => ({ url: `/deals/${publicId}/publish`, method: 'POST', body }),
      invalidatesTags: ['Deals', 'Analytics'],
    }),
    rejectDeal: builder.mutation({
      query: ({ publicId, reason }) => ({
        url: `/deals/${publicId}/reject`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['Deals', 'Analytics'],
    }),
    listAuditLogs: builder.query({
      query: (params) => ({ url: '/audit-logs', params }),
      providesTags: ['AuditLogs'],
    }),
    listCategories: builder.query({
      query: () => '/categories',
      providesTags: ['Categories'],
    }),
    createCategory: builder.mutation({
      query: (body) => ({
        url: '/categories',
        method: 'POST',
        body: {
          name: body.name,
          slug: body.slug,
          icon: body.icon,
          parentId: body.parentId ?? body.parent_id ?? null,
        },
      }),
      invalidatesTags: ['Categories'],
    }),
    updateCategory: builder.mutation({
      query: ({ categoryId, ...body }) => ({
        url: `/categories/${categoryId}`,
        method: 'PATCH',
        body: {
          name: body.name,
          slug: body.slug,
          icon: body.icon,
        },
      }),
      invalidatesTags: ['Categories'],
    }),
    broadcastNotification: builder.mutation({
      query: (body) => ({
        url: '/notifications/broadcast',
        method: 'POST',
        body: {
          title: body.title,
          body: body.body,
          type: body.type || 'system',
          actionUrl: body.actionUrl || body.action_url || null,
          audience: body.audience || 'all',
        },
      }),
    }),
    getAnalyticsOverview: builder.query({
      query: () => '/analytics/overview',
      providesTags: ['Analytics'],
    }),
    getAnalyticsDeals: builder.query({
      query: () => '/analytics/deals',
      providesTags: ['Analytics'],
    }),
    getAnalyticsUsers: builder.query({
      query: () => '/analytics/users',
      providesTags: ['Analytics'],
    }),
    getAnalyticsEngagement: builder.query({
      query: () => '/analytics/engagement',
      providesTags: ['Analytics'],
    }),
  }),
});

const DEAL_API_BASE = '/api/v1';

export async function uploadImage(file) {
  const token = localStorage.getItem('accessToken');
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${DEAL_API_BASE}/images/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ title: 'Upload failed' }));
    throw new Error(err.title || 'Upload failed');
  }

  return res.json();
}

export async function updateDealImages(publicId, imageUrls) {
  const token = localStorage.getItem('accessToken');

  const res = await fetch(`${DEAL_API_BASE}/deals/${publicId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ imageUrls }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ title: 'Update failed' }));
    throw new Error(err.title || 'Update failed');
  }

  return res.json();
}

export async function fetchDealDetail(publicId) {
  const token = localStorage.getItem('accessToken');

  const res = await fetch(`${DEAL_API_BASE}/deals/${publicId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) throw new Error('Failed to fetch deal');

  return res.json();
}

export const {
  useListCasesQuery,
  useTakeActionMutation,
  useListAppealsQuery,
  useReviewAppealMutation,
  useListMerchantsQuery,
  useCreateMerchantMutation,
  useUpdateMerchantMutation,
  useListUsersQuery,
  useUpdateUserMutation,
  useListFeatureFlagsQuery,
  useToggleFeatureFlagMutation,
  useListAffiliateProgramsQuery,
  useUpdateAffiliateProgramMutation,
  useListAllDealsQuery,
  useExpireDealMutation,
  usePublishDealMutation,
  useRejectDealMutation,
  useListAuditLogsQuery,
  useListCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useBroadcastNotificationMutation,
  useGetAnalyticsOverviewQuery,
  useGetAnalyticsDealsQuery,
  useGetAnalyticsUsersQuery,
  useGetAnalyticsEngagementQuery,
} = adminApi;
